import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// The store list came out of Google Places, so it carries capsule-toy shops,
// pachinko parlours and things that are not stores at all. This screen exists
// to settle them one by one from the photos, the same way a visitor would.

const STATES = [
  { value: 'pending', label: '未判定' },
  { value: 'auto_excluded', label: '自動で除外されたもの' },
  { value: 'hidden', label: '非表示にした店舗' },
  { value: 'confirmed', label: '確認済み' },
  { value: 'all', label: 'すべて' },
];

// Anything but no_crane is rare, so only "クレーンゲームなし" gets its own
// button; the rest sit behind one dropdown to keep the card readable.
const REASONS = [
  { value: 'no_crane', label: 'クレーンゲームなし' },
  { value: 'closed', label: '閉店' },
  { value: 'duplicate', label: '重複' },
  { value: 'not_a_store', label: '店舗ではない' },
  { value: 'wrong_data', label: 'データ誤り' },
  { value: 'other', label: 'その他' },
];

const REASON_LABELS = REASONS.reduce((acc, r) => ({ ...acc, [r.value]: r.label }), {});

const CRANE_CHECK_BADGES = {
  excluded_no_crane: { label: 'AI判定: クレーンなし', className: 'bg-danger-subtle text-danger-emphasis' },
  excluded_skipped: { label: 'AI判定: 対象外', className: 'bg-danger-subtle text-danger-emphasis' },
  analyzed: { label: 'AI解析済み', className: 'bg-info-subtle text-info-emphasis' },
  confirmed: { label: '人が確認済み', className: 'bg-success-subtle text-success-emphasis' },
};

const MACHINE_TYPE_LABELS = {
  bridge: '橋渡し',
  hook: 'フック',
  pera_ring: 'ペラ輪',
  suehirogari: '末広がり',
  kakuritsu: '確率機',
  pusher: 'プッシャー',
  cutting: '糸切り',
  takoyaki: 'たこ焼き',
  jewel_catcher: 'ジュエルキャッチャー',
  three_claw: '3本爪',
  two_claw: '2本爪',
};

const PREFECTURES = [
  '', '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
  '岐阜県', '静岡県', '愛知県', '三重県',
  '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
  '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県',
  '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

const PAGE_SIZE = 12;

// The headline numbers are kept in step locally rather than refetched: a
// refetch per click would cost a round trip and, worse, reshuffle the queue
// under the operator mid-scan.
//
// A store sits in exactly one bucket. Which one it was in before the click is
// what decides the decrement — restoring a hidden store has to take one off
// 非表示, not leave it there.
function bucketOf(store) {
  if (store.hiddenAt) return 'hidden';
  return store.reviewedAt ? 'confirmed' : 'pending';
}

function applyDecision(counts, store, hidden) {
  if (!counts) return counts;
  const from = bucketOf(store);
  const to = hidden ? 'hidden' : 'confirmed';
  if (from === to) return counts;
  return {
    ...counts,
    pending: Math.max(0, counts.pending - (from === 'pending' ? 1 : 0)),
    hidden: Math.max(0, counts.hidden + (to === 'hidden' ? 1 : 0) - (from === 'hidden' ? 1 : 0)),
    confirmed: Math.max(
      0,
      counts.confirmed + (to === 'confirmed' ? 1 : 0) - (from === 'confirmed' ? 1 : 0),
    ),
  };
}

// Undoing cannot put a store back in 未判定: the server stamps reviewed_at
// either way, so an undone "pending" store ends up confirmed. The counters say
// the same thing rather than showing a state the database no longer has.
function revertDecision(counts, store, hidden) {
  if (!counts) return counts;
  const from = hidden ? 'hidden' : 'confirmed';
  const to = store.hiddenAt ? 'hidden' : 'confirmed';
  if (from === to) return counts;
  return {
    ...counts,
    hidden: Math.max(0, counts.hidden + (to === 'hidden' ? 1 : 0) - (from === 'hidden' ? 1 : 0)),
    confirmed: Math.max(
      0,
      counts.confirmed + (to === 'confirmed' ? 1 : 0) - (from === 'confirmed' ? 1 : 0),
    ),
  };
}

// Moves one item, returning a new array.
function move(items, from, to) {
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Points at the crane-game words instead of making the operator read a
// paragraph to find them. That mention is usually the whole reason the card
// is decidable when the photos are ambiguous.
function highlightCraneWords(text, words) {
  if (!words || words.length === 0) return text;
  const set = new Set(words.map((w) => w.toLowerCase()));
  const parts = text.split(new RegExp(`(${words.map(escapeRegExp).join('|')})`, 'gi'));
  return parts.map((part, i) => (
    set.has(part.toLowerCase())
      ? <mark key={i} className="p-0">{part}</mark>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

export default function StoreReview() {
  useAuthGuard();

  const [sp, setSp] = useSearchParams();
  const [state, setState] = useState(sp.get('state') || 'pending');
  const [prefecture, setPrefecture] = useState(sp.get('prefecture') || '');
  const [keyword, setKeyword] = useState(sp.get('q') || '');
  const [queryKeyword, setQueryKeyword] = useState(sp.get('q') || '');
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [lastAction, setLastAction] = useState(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (state !== 'pending') p.set('state', state);
    if (prefecture) p.set('prefecture', prefecture);
    if (queryKeyword) p.set('q', queryKeyword);
    setSp(p, { replace: true });
  }, [state, prefecture, queryKeyword, setSp]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const url = new URL(`${API_BASE_URL}/admin/stores/review`);
      url.searchParams.set('state', state);
      url.searchParams.set('limit', String(PAGE_SIZE));
      url.searchParams.set('offset', String(offset));
      if (prefecture) url.searchParams.set('prefecture', prefecture);
      if (queryKeyword) url.searchParams.set('q', queryKeyword);

      const res = await apiFetch(url.toString(), { signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const data = body.data || {};
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
      setCounts(data.counts || null);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Network Error');
    } finally {
      setLoading(false);
    }
  }, [state, prefecture, queryKeyword, offset]);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  // A decided store leaves the queue immediately: waiting for a refetch would
  // make the operator lose their place every few clicks.
  const decide = async (store, hidden, reason) => {
    setSaving(store.id);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden, reason: reason || '' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setItems((prev) => prev.filter((s) => s.id !== store.id));
      setTotal((t) => Math.max(0, t - 1));
      setCounts((c) => applyDecision(c, store, hidden));
      setLastAction({
        store,
        hidden,
        label: hidden ? `「${store.name}」を非表示にしました（${REASON_LABELS[reason]}）` : `「${store.name}」を公開のままにしました`,
      });
    } catch (e) {
      alert('更新に失敗しました: ' + e.message);
    } finally {
      setSaving('');
    }
  };

  // Undo puts the store back the way it was, and back on screen: a misclick in
  // a queue of thousands must not need a search to repair.
  const undo = async () => {
    if (!lastAction) return;
    const { store, hidden } = lastAction;
    setSaving(store.id);
    try {
      const body = store.hiddenAt
        ? { hidden: true, reason: store.hiddenReason || 'other', note: store.hiddenNote || '' }
        : { hidden: false };
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => [store, ...prev]);
      setTotal((t) => t + 1);
      setCounts((c) => revertDecision(c, store, hidden));
      setLastAction(null);
    } catch (e) {
      alert('取り消しに失敗しました: ' + e.message);
    } finally {
      setSaving('');
    }
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <Layout>
      <div className="mb-3">
        <h2 className="page-title">店舗レビュー</h2>
        <p className="page-subtitle">
          写真を見てクレーンゲーム店かどうかを判定します。「クレーンゲームなし」等を押すと一般ユーザーから見えなくなります（削除ではありません）。
        </p>
      </div>

      {counts && (
        <div className="row g-2 mb-3">
          {[
            { label: '未判定', value: counts.pending, tone: 'text-warning-emphasis' },
            { label: 'うちAI除外', value: counts.autoExcluded, tone: 'text-danger-emphasis' },
            { label: '非表示', value: counts.hidden, tone: 'text-danger-emphasis' },
            { label: '確認済み', value: counts.confirmed, tone: 'text-success-emphasis' },
            { label: '全店舗', value: counts.total, tone: '' },
          ].map((c) => (
            <div className="col-6 col-md" key={c.label}>
              <div className="card h-100">
                <div className="card-body py-2 px-3">
                  <div style={{ fontSize: '0.75rem', color: '#8a94ad' }}>{c.label}</div>
                  <div className={`fw-bold ${c.tone}`} style={{ fontSize: '1.25rem' }}>
                    {new Intl.NumberFormat().format(c.value)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body py-2 px-3">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={state}
              onChange={(e) => { setState(e.target.value); setOffset(0); }}
            >
              {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={prefecture}
              onChange={(e) => { setPrefecture(e.target.value); setOffset(0); }}
            >
              {PREFECTURES.map((p) => <option key={p} value={p}>{p || '全国'}</option>)}
            </select>
            <form
              className="d-flex gap-2"
              onSubmit={(e) => { e.preventDefault(); setQueryKeyword(keyword); setOffset(0); }}
            >
              <input
                className="form-control form-control-sm"
                style={{ width: '220px' }}
                placeholder="店名・住所で絞り込み"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <button className="btn btn-phoenix-secondary btn-sm" type="submit">検索</button>
            </form>
            {state === 'pending' && (
              <span style={{ fontSize: '0.75rem', color: '#8a94ad' }}>
                写真がある店舗のみ表示しています
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 修正申請を先頭に置く。写真と景品は「足りないものが届いた」だが、
          こちらは「いま出ている情報が間違っている」で、放置している間ずっと
          嘘を表示し続けている。 */}
      <PendingEditRequests />
      <PendingPhotos />
      <PendingPrizes />

      {lastAction && (
        <div className="alert alert-secondary d-flex justify-content-between align-items-center py-2">
          <span>{lastAction.label}</span>
          <button className="btn btn-sm btn-phoenix-secondary" onClick={undo}>元に戻す</button>
        </div>
      )}

      {loading && <div className="card"><div className="card-body text-center py-5">読み込み中...</div></div>}
      {!loading && error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="card"><div className="card-body text-center py-5 text-muted">対象の店舗はありません</div></div>
      )}

      <div className="row g-3">
        {items.map((store) => (
          <div className="col-12 col-xl-6" key={store.id}>
            <StoreReviewCard
              store={store}
              saving={saving === store.id}
              onPhoto={setLightbox}
              onDecide={decide}
            />
          </div>
        ))}
      </div>

      <Pagination
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        itemCount={items.length}
        loading={loading}
        hasPrev={offset > 0}
        hasMore={offset + PAGE_SIZE < total}
        onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
        onNext={() => setOffset((o) => o + PAGE_SIZE)}
      />

      {lightbox && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(0,0,0,0.8)', zIndex: 2000, cursor: 'zoom-out' }}
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt=""
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain' }}
          />
        </div>
      )}
    </Layout>
  );
}

function StoreReviewCard({ store, saving, onPhoto, onDecide }) {
  const [showReasons, setShowReasons] = useState(false);
  const badge = CRANE_CHECK_BADGES[store.craneCheck];
  const mapsUrl = store.placeId
    ? `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}&query_place_id=${store.placeId}`
    : `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lng}`;

  return (
    <div className="card h-100">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div>
            <h5 className="mb-1" style={{ fontSize: '1rem' }}>{store.name}</h5>
            <div style={{ fontSize: '0.8125rem', color: '#525b75' }}>{store.address}</div>
          </div>
          <a
            className="btn btn-phoenix-secondary btn-sm text-nowrap"
            href={mapsUrl}
            target="_blank"
            rel="noreferrer"
          >
            地図
          </a>
        </div>

        <div className="d-flex flex-wrap gap-1 mb-2">
          {badge && <span className={`badge ${badge.className}`}>{badge.label}</span>}
          {store.hiddenAt && (
            <span className="badge bg-dark">
              非表示中{store.hiddenReason ? `: ${REASON_LABELS[store.hiddenReason] || store.hiddenReason}` : ''}
            </span>
          )}
          {store.chain && <span className="badge bg-secondary-subtle text-secondary-emphasis">{store.chain}</span>}
          {(store.machineTypes || []).map((t) => (
            <span className="badge bg-primary-subtle text-primary-emphasis" key={t}>
              {MACHINE_TYPE_LABELS[t] || t}
            </span>
          ))}
          {store.photoTotal > 0 && (
            <span className="badge bg-light text-secondary">写真 {store.photoTotal}枚</span>
          )}
          {(store.evidenceHasCrane > 0 || store.evidenceNoCrane > 0) && (
            <span
              className={`badge ${
                store.evidenceHasCrane > 0
                  ? 'bg-success-subtle text-success-emphasis'
                  : 'bg-warning-subtle text-warning-emphasis'
              }`}
            >
              根拠 あり{store.evidenceHasCrane} / なし{store.evidenceNoCrane}
            </span>
          )}
          {store.googleReviewTotal > 0 && (
            <span
              className={`badge ${
                store.craneReviewCount > 0
                  ? 'bg-success-subtle text-success-emphasis'
                  : 'bg-light text-secondary'
              }`}
            >
              クチコミ {store.craneReviewCount}/{store.googleReviewTotal}件がクレーン言及
            </span>
          )}
        </div>

        <PhotoStrip storeId={store.id} photos={store.photos} onPhoto={onPhoto} />

        {(store.prizeNames || []).length > 0 && (
          <div className="mb-2" style={{ fontSize: '0.8125rem', color: '#525b75' }}>
            景品: {store.prizeNames.slice(0, 5).join('、')}
          </div>
        )}

        {(store.reviews || []).map((r, i) => (
          <div key={i} className="mb-1" style={{ fontSize: '0.8125rem', color: '#525b75' }}>
            <span className="text-warning">{'\u2605'.repeat(r.rating)}</span>{' '}
            <span>{highlightCraneWords(r.text, r.craneWords)}</span>
          </div>
        ))}

        <EvidencePanel store={store} />
        <MachineCountPanel store={store} />
        <HistoryPanel store={store} />

        <div className="d-flex flex-wrap gap-2 mt-3">
          <button
            className="btn btn-success btn-sm"
            disabled={saving}
            onClick={() => onDecide(store, false)}
          >
            クレーンゲームあり（公開）
          </button>
          <button
            className="btn btn-danger btn-sm"
            disabled={saving}
            onClick={() => onDecide(store, true, 'no_crane')}
          >
            クレーンゲームなし（非表示）
          </button>
          <button
            className="btn btn-phoenix-secondary btn-sm"
            disabled={saving}
            onClick={() => setShowReasons((v) => !v)}
          >
            別の理由で非表示
          </button>
        </div>

        {showReasons && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {REASONS.filter((r) => r.value !== 'no_crane').map((r) => (
              <button
                key={r.value}
                className="btn btn-outline-danger btn-sm"
                disabled={saving}
                onClick={() => onDecide(store, true, r.value)}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// The first photo is the store's face: it is the cover on every list card and
// the opening shot on the store page. Imports keep whatever order Google
// returned, so the crane-game floor often sits behind a shot of the mall
// entrance. The operator is already looking at these photos to make the call,
// so fixing the cover costs one drag.
function PhotoStrip({ storeId, photos, onPhoto }) {
  const [order, setOrder] = useState(photos || []);
  const [dragFrom, setDragFrom] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { setOrder(photos || []); }, [photos]);

  if (order.length === 0) {
    return <div className="text-muted mb-2" style={{ fontSize: '0.8125rem' }}>写真なし</div>;
  }

  // A store with no gallery falls back to its Google cover image, which has no
  // store_photos row and so no id — nothing to reorder, and nothing to reorder
  // it with.
  const canReorder = order.length > 1 && order.every((p) => p.id);

  const save = async (next) => {
    const previous = order;
    setOrder(next);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${storeId}/photos/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photoIds: next.map((p) => p.id) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      // Put the strip back rather than leaving the screen disagreeing with
      // what everyone else will see.
      setOrder(previous);
      setError('並び順を保存できませんでした: ' + e.message);
    }
  };

  const drop = (to) => {
    if (dragFrom === null || dragFrom === to) return;
    const from = dragFrom;
    setDragFrom(null);
    save(move(order, from, to));
  };

  return (
    <div className="mb-2">
      <div className="d-flex gap-1" style={{ overflowX: 'auto' }}>
        {order.map((photo, i) => (
          <div
            key={photo.id || photo.url}
            className="position-relative"
            style={{ flex: '0 0 auto' }}
            draggable={canReorder}
            onDragStart={() => setDragFrom(i)}
            onDragEnd={() => setDragFrom(null)}
            onDragOver={(e) => canReorder && e.preventDefault()}
            onDrop={() => drop(i)}
          >
            <img
              src={photo.url}
              alt=""
              loading="lazy"
              onClick={() => onPhoto(photo.url)}
              style={{
                width: '120px', height: '90px', objectFit: 'cover',
                borderRadius: '4px', display: 'block',
                cursor: canReorder ? 'grab' : 'zoom-in',
                opacity: dragFrom === i ? 0.4 : 1,
              }}
            />
            {canReorder && i === 0 && (
              <span
                className="badge bg-dark position-absolute"
                style={{ top: '4px', left: '4px', fontSize: '0.625rem' }}
              >
                表紙
              </span>
            )}
            {canReorder && i > 0 && (
              <button
                type="button"
                className="btn btn-dark btn-sm position-absolute d-flex align-items-center justify-content-center p-0"
                style={{ top: '4px', left: '4px', width: '22px', height: '22px', lineHeight: 1 }}
                title="表紙にする"
                aria-label={`この写真を表紙にする（${i + 1}枚目）`}
                onClick={() => save(move(order, i, 0))}
              >
                &#8676;
              </button>
            )}
          </div>
        ))}
      </div>
      {canReorder && (
        <div style={{ fontSize: '0.75rem', color: '#8a94ad' }}>
          ドラッグで並び替え。先頭の写真が一覧のサムネイルになります
        </div>
      )}
      {error && <div className="text-danger" style={{ fontSize: '0.75rem' }}>{error}</div>}
    </div>
  );
}

const EVIDENCE_SOURCE_LABELS = {
  photo_ai: '写真解析',
  google_review: 'クチコミ',
  x_post: 'X',
  official_site: '公式サイト',
  manual: '運営',
};

/**
 * Where a store's machine count came from, and everything it said before.
 *
 * The public page shows one number and the page it was read from. This is for
 * the person who has to answer 「この数字はどこから来た？」 — when a store's own
 * number changes, when one turns out to be wrong, and when deciding whether a
 * count read off a chain's API two months ago still stands.
 */
function MachineCountPanel({ store }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}/machine-counts`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setData(body.data || { items: [], current: {} });
    } catch (e) {
      setError('台数の履歴を読み込めませんでした: ' + e.message);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data === null) load();
  };

  const current = (data && data.current) || {};
  const items = (data && data.items) || [];

  // 数えた結果を入れる。crane_count には置けない（写真解析の集計が上書きする）。
  const [count, setCount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const saveCounted = async () => {
    const n = parseInt(count, 10);
    if (!n || n <= 0) return setError('台数を入れてください');
    if (!note.trim()) {
      return setError('数え方と、店内を網羅できている根拠を書いてください');
    }
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch(
        `${API_BASE_URL}/admin/stores/${store.id}/counted-machine-count`,
        { method: 'PUT', body: JSON.stringify({ count: n, note: note.trim() }) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCount('');
      setNote('');
      await load();
    } catch (e) {
      setError('保存できませんでした: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2">
      <button className="btn btn-phoenix-secondary btn-sm" type="button" onClick={toggle}>
        {open ? '台数の出典を閉じる' : '台数の出典・履歴'}
      </button>

      {open && (
        <div className="mt-2 p-2 rounded" style={{ background: '#f5f7fa' }}>
          {error && <div className="text-danger" style={{ fontSize: '0.75rem' }}>{error}</div>}
          {data === null && !error && (
            <div className="text-muted" style={{ fontSize: '0.8125rem' }}>読み込み中...</div>
          )}

          {data !== null && (
            <div style={{ fontSize: '0.8125rem' }}>
              <div className="mb-1">
                <strong>いま表示している値:</strong>{' '}
                {current.counted
                  ? `${current.counted}台（動画から数えた）`
                  : current.count
                    ? `${current.count}台${current.atLeast ? '以上' : ''}`
                    : '（台数なし）'}
                {current.scale && (
                  <span className="text-muted">
                    {' '}／ 規模 {current.scale}（根拠 {current.scaleBasis}）
                  </span>
                )}
              </div>
              {current.countedNote && (
                <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                  数え方: {current.countedNote}
                </div>
              )}

              <div className="mb-2 p-2 rounded" style={{ background: '#fff' }}>
                <div className="mb-1"><strong>動画から数えた台数を入れる</strong></div>
                <div className="d-flex gap-2 align-items-start">
                  <input
                    className="form-control form-control-sm"
                    style={{ width: '6rem' }}
                    type="number"
                    min="1"
                    placeholder="台数"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                  />
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder="数え方と、店内を網羅できている根拠（例: 店内歩き撮り4本248フレーム。プレイ単位＝ボタン群1つ＋取り出し口1つ）"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <button
                    className="btn btn-phoenix-primary btn-sm"
                    type="button"
                    disabled={saving}
                    onClick={saveCounted}
                  >
                    {saving ? '保存中...' : '保存'}
                  </button>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  網羅していない動画から数えた数は実数より必ず少なく出ます。
                  根拠が書けないときは入れないでください
                </div>
              </div>

              {items.length === 0 && (
                <div className="text-muted">
                  観測がありません。公表値が見つからなかった店舗です
                </div>
              )}

              {items.map((it) => (
                <div key={it.id} className="py-1 border-top">
                  <div className="d-flex gap-2 align-items-center">
                    <span className="badge bg-light text-secondary">
                      {it.sourceLabel || it.source}
                    </span>
                    <strong>
                      {it.count}台{it.atLeast ? '以上' : ''}
                    </strong>
                    <span className="text-muted">
                      {new Date(it.collectedAt).toLocaleString('ja-JP')}
                    </span>
                  </div>
                  {it.snippet && (
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      「{it.snippet}」
                    </div>
                  )}
                  {it.url && (
                    <a href={it.url} target="_blank" rel="noopener noreferrer nofollow"
                       style={{ fontSize: '0.75rem' }}>
                      出典ページ
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Everything recorded about why this store is, or is not, a crane-game store —
 * and a form to add what the operator just found.
 *
 * The judgement that costs the most is a person searching the web for a store
 * the photos cannot settle. Before this, that judgement was thrown away the
 * moment they clicked, and the next person to see the store started over.
 */
function EvidencePanel({ store }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ source: 'x_post', verdict: 'has_crane', url: '', snippet: '' });

  const total = (store.evidenceHasCrane || 0) + (store.evidenceNoCrane || 0);

  const load = async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}/evidence`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems((body.data && body.data.items) || []);
    } catch (e) {
      setError('根拠を読み込めませんでした: ' + e.message);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && items === null) load();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.snippet.trim()) return;
    setAdding(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}/evidence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setForm({ ...form, url: '', snippet: '' });
      await load();
    } catch (e2) {
      setError('保存できませんでした: ' + e2.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="mt-2">
      <button className="btn btn-phoenix-secondary btn-sm" type="button" onClick={toggle}>
        {open ? '根拠を閉じる' : total > 0 ? `根拠を見る（${total}件）` : '根拠を追加'}
      </button>

      {open && (
        <div className="mt-2 p-2 rounded" style={{ background: '#f5f7fa' }}>
          {error && <div className="text-danger" style={{ fontSize: '0.75rem' }}>{error}</div>}

          {items === null && <div className="text-muted" style={{ fontSize: '0.8125rem' }}>読み込み中...</div>}
          {items !== null && items.length === 0 && (
            <div className="text-muted" style={{ fontSize: '0.8125rem' }}>まだ根拠がありません</div>
          )}

          {(items || []).map((it) => (
            <div key={it.id} className="d-flex gap-2 py-1" style={{ fontSize: '0.8125rem' }}>
              <span
                className={`badge ${
                  it.verdict === 'has_crane'
                    ? 'bg-success-subtle text-success-emphasis'
                    : 'bg-warning-subtle text-warning-emphasis'
                }`}
                style={{ height: 'fit-content' }}
              >
                {EVIDENCE_SOURCE_LABELS[it.source] || it.source}
              </span>
              <span style={{ color: '#525b75' }}>
                {it.snippet}
                {it.author && <span className="text-muted"> — {it.author}</span>}
                {it.url && (
                  <a className="ms-1" href={it.url} target="_blank" rel="noreferrer">出典</a>
                )}
              </span>
            </div>
          ))}

          {/* Reading X in bulk needs a paid key; an operator who has just found
              the answer in a search result can keep it in ten seconds. */}
          <form className="d-flex flex-wrap gap-2 mt-2 align-items-center" onSubmit={submit}>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
            >
              <option value="x_post">X の投稿</option>
              <option value="official_site">公式サイト</option>
              <option value="manual">運営メモ</option>
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={form.verdict}
              onChange={(e) => setForm({ ...form, verdict: e.target.value })}
            >
              <option value="has_crane">クレーンあり</option>
              <option value="no_crane">クレーンなし</option>
            </select>
            <input
              className="form-control form-control-sm"
              style={{ width: '260px' }}
              placeholder="URL（任意）"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
            />
            <input
              className="form-control form-control-sm"
              style={{ flex: '1 1 240px', minWidth: '200px' }}
              placeholder="引用・メモ（必須）"
              value={form.snippet}
              onChange={(e) => setForm({ ...form, snippet: e.target.value })}
            />
            <button className="btn btn-primary btn-sm" type="submit" disabled={adding}>
              追加
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * Photos visitors offered for a store, waiting to be looked at.
 *
 * Approving puts the image on the store's page — often as its first photo,
 * which is the store's face across the whole site. Nothing appears until
 * somebody here says so.
 *
 * It sits on this screen rather than one of its own: uploads arrive rarely, and
 * a page nobody opens is a queue nobody clears.
 */
function PendingPhotos() {
  const [photos, setPhotos] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/photos/pending?limit=50`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPhotos((await res.json()).data || []);
    } catch {
      setPhotos([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const moderate = async (photo, approve) => {
    setBusy(photo.id);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/photos/${photo.id}/moderate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approve }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch (e) {
      alert('更新に失敗しました: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  if (!photos || photos.length === 0) return null;

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h5 className="mb-2" style={{ fontSize: '0.9375rem' }}>
          ユーザー投稿写真の確認（{photos.length}件）
        </h5>
        <p className="text-muted mb-2" style={{ fontSize: '0.8125rem' }}>
          承認すると店舗ページに掲載されます。1枚目になると一覧のサムネイルにもなります。
        </p>
        <div className="d-flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.id} style={{ width: 180 }}>
              <img
                src={p.photoUrl}
                alt=""
                onClick={() => window.open(p.photoUrl, '_blank', 'noopener')}
                style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }}
              />
              <div className="d-flex gap-1 mt-1">
                <button
                  className="btn btn-success btn-sm flex-fill"
                  disabled={busy === p.id}
                  onClick={() => moderate(p, true)}
                >
                  掲載する
                </button>
                <button
                  className="btn btn-outline-danger btn-sm flex-fill"
                  disabled={busy === p.id}
                  onClick={() => moderate(p, false)}
                >
                  却下
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 店舗ページから届いた景品報告の確認待ち。
 *
 * 写真と同じ場所に置く。専用ページを作ると開かれないキューになるし、
 * どちらも「店舗ページの穴に来た人が答えてくれたもの」で、判断のしかたも同じ。
 */
function PendingPrizes() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-prizes/pending?limit=50`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((await res.json()).data || []);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const moderate = async (row, approve) => {
    setBusy(row.id);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-prizes/${row.id}/moderate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approve }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert('更新に失敗しました: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h5 className="mb-2" style={{ fontSize: '0.9375rem' }}>
          ユーザー投稿の景品報告（{rows.length}件）
        </h5>
        <p className="text-muted mb-2" style={{ fontSize: '0.8125rem' }}>
          承認すると店舗の「獲得プライズ」に出ます。景品名は投稿された文字そのままです。
        </p>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ width: '35%' }}>{r.store?.name || '—'}</td>
                  <td>{r.prize?.name || '—'}</td>
                  <td style={{ width: 90 }}>{PRIZE_STATUS_LABEL[r.status] || r.status}</td>
                  <td style={{ width: 170 }}>
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-success btn-sm flex-fill"
                        disabled={busy === r.id}
                        onClick={() => moderate(r, true)}
                      >
                        掲載する
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm flex-fill"
                        disabled={busy === r.id}
                        onClick={() => moderate(r, false)}
                      >
                        却下
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PRIZE_STATUS_LABEL = {
  available: '今ある',
  upcoming: '入荷予定',
  sold_out: 'もう無い',
  unknown: '不明',
};

/**
 * 店舗ページから届いた「情報の修正を依頼」の判定待ち。
 *
 * 承認すると stores が書き換わり、公開ページの表示が変わる。写真や景品の承認と
 * 違って**すでに出ている値を上書きする**ので、判断に要るのは提案値だけではなく
 * 「送信者が何を見てそう言ったのか」＝ current_value のほう。
 *
 * 申請から判定までの間に値が変わることがある（Google の再取得・運営の直接編集・
 * 先に承認された別の申請）。いまの値と申請時の値がずれていたら、その場で気づけ
 * ないと**新しいほうを古い提案で上書きする**。並べて出しているのはそのため。
 *
 * 提案値はそのまま採らずに直せる（「１１時」→「11:00」）。直した値は
 * applied_value に残るので、後から「何を提案され、何を採ったか」を追える。
 */
function PendingEditRequests() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const [edited, setEdited] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-edit-requests/pending?limit=50`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows((await res.json()).data || []);
    } catch {
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const moderate = async (row, approve) => {
    // 却下は理由を聞く。理由の無い却下は、同じことを次に報せてくる人に
    // 同じ沈黙を返すだけになる
    let rejectReason = '';
    if (!approve) {
      rejectReason = window.prompt('却下の理由（送信者には表示しません。記録用）', '') ?? '';
    }

    setBusy(row.id);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/store-edit-requests/${row.id}/moderate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approve,
          appliedValue: approve ? (edited[row.id] ?? '') : '',
          rejectReason,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      alert('更新に失敗しました: ' + e.message);
    } finally {
      setBusy('');
    }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="card mb-3">
      <div className="card-body p-3">
        <h5 className="mb-2" style={{ fontSize: '0.9375rem' }}>
          店舗情報の修正申請（{rows.length}件）
        </h5>
        <p className="text-muted mb-2" style={{ fontSize: '0.8125rem' }}>
          承認すると店舗ページの表示がその場で変わります。提案値は直してから採れます。
          却下しても行は残ります（誰が何を見たかの記録）。
        </p>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr className="text-muted" style={{ fontSize: '0.75rem' }}>
                <th style={{ width: '22%' }}>店舗</th>
                <th style={{ width: 110 }}>項目</th>
                <th style={{ width: '20%' }}>いまの値</th>
                <th>提案値</th>
                <th style={{ width: 170 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // 申請時に見えていた値と、いまの値。ずれていたら承認前に気づく
                // 必要がある——新しいほうを古い提案で上書きしてしまうため
                const nowValue = r.store ? STORE_FIELD_VALUE(r.store, r.field) : null;
                const drifted =
                  nowValue !== null && (r.currentValue ?? '') !== nowValue;

                return (
                  <tr key={r.id}>
                    <td>
                      {r.store?.name || '—'}
                      {r.note && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          「{r.note}」
                        </div>
                      )}
                    </td>
                    <td>{r.label || r.field}</td>
                    <td>
                      <span style={{ fontSize: '0.8125rem' }}>
                        {r.currentValue || <span className="text-muted">（未登録）</span>}
                      </span>
                      {drifted && (
                        <div className="text-warning" style={{ fontSize: '0.75rem' }}>
                          申請後に変更されています → 現在「{nowValue || '未登録'}」
                        </div>
                      )}
                    </td>
                    <td>
                      {r.applies === false ? (
                        <span className="badge bg-danger-subtle text-danger">
                          閉店の報告（承認すると非表示）
                        </span>
                      ) : (
                        <input
                          className="form-control form-control-sm"
                          defaultValue={r.proposedValue || ''}
                          onChange={(e) =>
                            setEdited((prev) => ({ ...prev, [r.id]: e.target.value }))
                          }
                        />
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-success btn-sm flex-fill"
                          disabled={busy === r.id}
                          onClick={() => moderate(r, true)}
                        >
                          反映する
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm flex-fill"
                          disabled={busy === r.id}
                          onClick={() => moderate(r, false)}
                        >
                          却下
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * いまサーバーが返している店舗の、その項目の値。
 *
 * 表示だけに使う（承認時に書き込む値はサーバーが domain.CurrentStoreFieldValue
 * から読む）。ここでの取りこぼしは「ずれの警告が出ない」で済み、間違った値を
 * 書くことはない。キーは API の json タグ（camelCase）。
 */
function STORE_FIELD_VALUE(store, field) {
  switch (field) {
    case 'name': return store.name ?? '';
    case 'address': return store.address ?? '';
    case 'phone': return store.phone ?? '';
    case 'website_url': return store.websiteUrl ?? '';
    case 'open_time': return store.openTime ?? '';
    case 'close_time': return store.closeTime ?? '';
    case 'business_hours': return store.businessHours ?? '';
    case 'play_price': return store.playPrice != null ? String(store.playPrice) : '';
    case 'parking': return store.hasParking ? 'true' : 'false';
    case 'photo_allowed': return store.photoAllowed ? 'true' : 'false';
    default: return null;
  }
}

/**
 * 「いつ誰が何をどう変えたか」（L-6）。
 *
 * `store_field_changes` は Google の再取得・運営の編集・店舗ポータル・修正申請の
 * 承認の4経路から書かれていたが、**読む画面が無かった**。差し戻すかどうかを
 * 決めるのに要るのは、いまの値ではなく「前は何だったか」と「誰が入れたか」。
 *
 * 1行1列の変化。営業時間の JSON のように長い値は畳んで出す——生の JSON を
 * 全文並べると、その1行で他の変化が画面から押し出される。
 */
function HistoryPanel({ store }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${store.id}/field-changes`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setItems((body.data && body.data.items) || []);
    } catch (e) {
      setError('変更履歴を読み込めませんでした: ' + e.message);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && items === null) load();
  };

  return (
    <div className="mt-2">
      <button className="btn btn-phoenix-secondary btn-sm" type="button" onClick={toggle}>
        {open ? '変更履歴を閉じる' : '変更履歴'}
      </button>

      {open && (
        <div className="mt-2 p-2 rounded" style={{ background: '#f5f7fa' }}>
          {error && <div className="text-danger" style={{ fontSize: '0.75rem' }}>{error}</div>}
          {items === null && !error && (
            <div className="text-muted" style={{ fontSize: '0.8125rem' }}>読み込み中...</div>
          )}
          {items !== null && items.length === 0 && (
            <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
              {/* 履歴は 2026-09-10（migration 000081）から。それ以前の値は
                  誰が入れたか分からないので、空欄を「変わっていない」と
                  読ませない */}
              記録された変更はありません（履歴は2026-09-10以降の更新ぶん）
            </div>
          )}
          {(items || []).map((it) => (
            <HistoryRow key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ item }) {
  const [expanded, setExpanded] = useState(false);
  const size = (item.oldValue || '').length + (item.newValue || '').length;
  const long = size > 120;

  return (
    <div className="py-1 border-bottom" style={{ fontSize: '0.8125rem' }}>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="badge bg-light text-secondary">{item.fieldLabel}</span>
        <span className="badge bg-secondary-subtle text-secondary-emphasis">
          {item.sourceLabel}
        </span>
        {/* 実行者が無いのは「人ではない経路」。空欄のままにして、
            Google や一括処理を誰かの仕業に見せない */}
        {item.changedBy && <span style={{ color: '#525b75' }}>{item.changedBy}</span>}
        <span className="text-muted ms-auto">
          {new Date(item.changedAt).toLocaleString('ja-JP')}
        </span>
      </div>

      <div className="mt-1" style={{ color: '#525b75', wordBreak: 'break-all' }}>
        {long && !expanded ? (
          <button
            className="btn btn-link btn-sm p-0"
            type="button"
            onClick={() => setExpanded(true)}
          >
            値を見る（{size}文字）
          </button>
        ) : (
          <>
            <span className="text-decoration-line-through text-muted">
              {item.oldValue || '（空）'}
            </span>
            {' → '}
            <span>{item.newValue || '（空）'}</span>
          </>
        )}
      </div>

      {item.note && (
        <div className="text-muted" style={{ fontSize: '0.75rem' }}>{item.note}</div>
      )}
    </div>
  );
}
